import { Command } from 'commander';
import chalk from 'chalk';
import fs from 'fs-extra';
import { join } from 'path';
import { logger } from '../lib/logger.js';
import { updateCommands, checkInstallation, purgeRalphProjectArtifacts } from '../lib/installer.js';
import { getCliVersion, readInstalledSkillsVersion, PROJECT_CONFIG_REL } from '../lib/project-versions.js';

/** True when `dir` is a SmartStack project root (it carries .smartstack/config.json). */
async function isSmartStackProject(dir: string): Promise<boolean> {
  return fs.pathExists(join(dir, ...PROJECT_CONFIG_REL.split('/')));
}

export const updateCommand = new Command('update')
  .description('Update the deployed skills, agents and hooks to this CLI version (a PROJECT is upgraded by ss upgrade)')
  .option('-g, --global', 'Update user directory ~/.claude (default)', true)
  .option('-l, --local', 'Update project directory ./.claude')
  .option('-n, --dry-run', 'Preview changes without modifying files')
  .option('--check', 'Only compare the deployed skills version with this CLI version (exit 1 when they differ)')
  .action(async (options) => {
    const dryRun = options.dryRun ?? false;
    const isGlobal = !options.local;
    const cliVersion = getCliVersion();
    const inProject = await isSmartStackProject(process.cwd());

    if (options.check) {
      const installed = await readInstalledSkillsVersion({ global: isGlobal });
      const where = isGlobal ? '~/.claude' : './.claude';
      if (installed === cliVersion) {
        logger.success(`Skills in ${where} are at ${chalk.cyan(cliVersion)} — same as the CLI.`);
        return;
      }
      logger.warning(
        installed
          ? `Skills in ${where} are at ${chalk.yellow(installed)}, the CLI is ${chalk.cyan(cliVersion)} — run ${chalk.cyan('ss update')}.`
          : `No SmartStack skills deployed in ${where} — run ${chalk.cyan('ss install')}.`,
      );
      process.exitCode = 1;
      return;
    }

    if (dryRun) {
      logger.header('SmartStack Update - DRY RUN');
    } else {
      logger.header('Updating SmartStack Commands');
    }

    if (inProject) {
      logger.info(
        `${chalk.gray('Tip:')} ss update only refreshes the skills. To upgrade THIS project (NuGet/npm socle, ` +
          `configuration, code migrations, regeneration) run ${chalk.cyan('ss upgrade')} — it also syncs the skills.`,
      );
    }

    const installation = await checkInstallation({ global: isGlobal });

    if (!installation.installed) {
      logger.warning('No existing installation detected — running full install with force...');
    }

    const spinner = dryRun ? null : logger.spinner('Updating commands...');

    try {
      const result = await updateCommands({
        global: isGlobal,
        dryRun,
      });

      if (spinner) {
        spinner.succeed('Commands updated successfully!');
      }

      // Remove the legacy .ralph/ directory — only inside a SmartStack project: from any
      // other folder (home, a repo of another stack) a `.ralph/` is none of our business.
      if (!dryRun && inProject) {
        await purgeRalphProjectArtifacts();
      }

      // Display summary
      logger.info('');
      logger.info('Update Summary:');
      logger.info(`  Files updated: ${result.updated}`);
      logger.info(`  Files removed: ${result.removed}`);
      // Each removal is already logged by updateCommands — no second listing.

      if (result.errors.length > 0) {
        logger.info('');
        logger.warning(`Errors: ${result.errors.length}`);
        for (const err of result.errors) {
          logger.error(`  ${err}`);
        }
      }

      if (dryRun && result.removed > 0) {
        logger.info('');
        logger.info('Run without --dry-run to apply changes.');
      }
    } catch (error) {
      if (spinner) {
        spinner.fail('Update failed');
      }
      logger.error(error instanceof Error ? error.message : 'Unknown error');
      process.exit(1);
    }
  });
